import type { TemplateFormFieldDef } from '@klareospace/pharos-lib-types';
import { z } from 'zod/v4';

// ─── Default values ───────────────────────────────────────────────────────────

/**
 * Builds the initial form values from a template's field definitions.
 * When `existingValues` is provided (Edit flow), those values take precedence
 * over `defaultValue` from the field definition.
 */
export function buildDefaultValues(
  fields: TemplateFormFieldDef[],
  existingValues?: Record<string, unknown>,
): Record<string, unknown> {
  const defaults: Record<string, unknown> = {};
  for (const field of fields) {
    const existing = existingValues?.[field.key];
    if (existing !== undefined) {
      defaults[field.key] = existing;
    } else if (field.defaultValue !== undefined) {
      defaults[field.key] = field.defaultValue;
    } else {
      defaults[field.key] = '';
    }
  }
  return defaults;
}

// ─── Zod schema builder ───────────────────────────────────────────────────────

/**
 * Builds a Zod schema at runtime from the template's serialized `formFields`.
 * Each field's `required` flag and `validation` descriptor (minLength, maxLength,
 * pattern, min, max) are translated to Zod refinements declaratively — no eval,
 * no functions embedded in the descriptor.
 */
export function buildZodSchemaForFields(fields: TemplateFormFieldDef[]): z.ZodObject<Record<string, z.ZodTypeAny>> {
  const shape: Record<string, z.ZodTypeAny> = {};
  for (const field of fields) {
    shape[field.key] = buildFieldSchema(field);
  }
  return z.object(shape);
}

export function buildFieldSchema(field: TemplateFormFieldDef): z.ZodTypeAny {
  const { options, required, type, validation } = field;
  if (type === 'number') {
    return buildNumberFieldSchema(required, validation);
  }
  if (type === 'select') {
    return buildSelectFieldSchema(required, options);
  }
  // dynamic-select: options are resolved at runtime from remote data — no
  // compile-time enum is possible. Validate as a plain non-empty string when
  // required; allow any string (including empty) when optional.
  if (type === 'dynamic-select') {
    return required ? z.string().min(1) : z.string().optional();
  }
  return buildStringFieldSchema(required, type, validation);
}

// ─── Field schema helpers ─────────────────────────────────────────────────────

function buildNumberFieldSchema(required: boolean, validation: TemplateFormFieldDef['validation']): z.ZodTypeAny {
  // Preprocess: treat empty string (blank input) as undefined so that a blank
  // required number fails validation (required error) and a blank optional
  // number is omitted from the payload rather than coerced to 0.
  let base = z.number();
  if (validation?.min !== undefined) {
    base = base.min(validation.min);
  }
  if (validation?.max !== undefined) {
    base = base.max(validation.max);
  }
  const numberOrUndefined = z.preprocess(
    (val) => (val === '' || val === null ? undefined : Number(val)),
    required ? base : base.optional(),
  );
  return numberOrUndefined;
}

function buildSelectFieldSchema(required: boolean, options: TemplateFormFieldDef['options']): z.ZodTypeAny {
  const values = (options ?? []).map((o) => o.value);
  if (values.length === 0) {
    // No declared options — fall back to plain string validation.
    return required ? z.string().min(1) : z.string().optional();
  }
  // z.enum requires a non-empty tuple at compile time; we cast safely after the
  // runtime length guard above.
  const enumSchema = z.enum(values as [string, ...string[]]);
  if (!required) {
    return enumSchema.optional();
  }
  return enumSchema;
}

function buildStringFieldSchema(
  required: boolean,
  type: TemplateFormFieldDef['type'],
  validation: TemplateFormFieldDef['validation'],
): z.ZodTypeAny {
  let schema = z.string();
  if (type === 'url') {
    schema = schema.url();
  }
  if (validation?.minLength !== undefined) {
    schema = schema.min(validation.minLength);
  }
  if (validation?.maxLength !== undefined) {
    schema = schema.max(validation.maxLength);
  }
  if (validation?.pattern) {
    schema = schema.regex(new RegExp(validation.pattern));
  }
  if (required) {
    return schema.min(1);
  }
  return schema.optional();
}
